import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import '@/styles/UserManagementStyles.scss';
import { FaEdit, FaEnvelope, FaSave, FaSearch, FaSpinner, FaStar, FaTimes, FaUsers, FaUser, FaBuilding, FaSitemap, FaCog, FaKey } from 'react-icons/fa';
import { dashboardOptions } from '@/constants/dashboardOptions';
import * as Sentry from '@sentry/react';
/**
 * Helper function to handle session expiration errors.
 * Checks if error is a session/auth error and triggers logout if needed.
 * @param {Error} error - The error that occurred
 * @param {Function} setSessionExpired - State setter for session expired flag
 * @param {Function} logout - Logout function from auth context
 */
const handleSessionExpiration = (error, setSessionExpired, logout) => {
    // Check for common session expiration indicators
    if (error?.message?.includes('401') || 
        error?.message?.includes('unauthorized') || 
        error?.message?.includes('session') ||
        error?.response?.status === 401) {
        setSessionExpired(true);
        logout();
    }
};

/**
 * Utility function to format account expiry date for datetime-local input.
 * Formats UTC datetime string to ISO format truncated for datetime-local input (YYYY-MM-DDTHH:mm).
 * Note: The datetime-local input interprets the value as local time, so browsers will display
 * the UTC time as if it were in the user's timezone. The value is stored in UTC on the backend.
 * @param {string} utcDateString - The UTC datetime string from the API
 * @returns {string} Formatted datetime string for datetime-local input, or empty string if null
 */
const formatExpiryDateForInput = (utcDateString) => {
    if (!utcDateString) return '';
    return new Date(utcDateString).toISOString().slice(0, 16);
};

/**
 * Helper function to format expiry date for display in the table.
 * Creates a Date object once and returns both the tooltip and display text.
 * @param {string} dateString - The UTC datetime string from the API
 * @returns {Object} Object with `tooltip` and `display` properties, or null if no date
 */
const formatExpiryDateForDisplay = (dateString) => {
    if (!dateString) return null;
    const expiryDate = new Date(dateString);
    return {
        tooltip: expiryDate.toLocaleString(),
        display: expiryDate.toLocaleDateString()
    };
};

/**
 * ViewUsers Component
 * 
 * Displays a list of all users with the ability to:
 * - Filter by active status
 * - Search by name or email
 * - Edit user details (department, manager, role, etc.)
 */
const ViewUsers = ({ refreshTrigger }) => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    // Default to showing only active users — inactive users are an edge case and
    // cluttering the list by default creates unnecessary noise.
    const [showOnlyActive, setShowOnlyActive] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const navigate = useNavigate();
    const { apiFetch, logout, user: currentUser, refreshUser } = useAuth();
    const [sessionExpired, setSessionExpired] = useState(false);

    // State for editing
    const [editingUser, setEditingUser] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [managers, setManagers] = useState([]); // Potential managers (excluding the user being edited)
    const [functions, setFunctions] = useState([]); // Available functions for assignment
    const [userFunctions, setUserFunctions] = useState([]); // Functions assigned to the user being edited
    const [savingUser, setSavingUser] = useState(false);
    const [sendingPasswordEmail, setSendingPasswordEmail] = useState(false);
    const [reenableEmailConflict, setReenableEmailConflict] = useState(false);
    const [editForm, setEditForm] = useState({
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        roleID: null,
        isActive: true,
        departmentID: null,
        managerUserID: null,
        isDirector: false,
        homeDashboardRoute: '/home',
        accountExpiryDate: ''
    });


    // Fetches the users from the database
    const GetUsers = useCallback(async () => {
        try {
            const response = await apiFetch("/api/User/view");
            const data = await response.json();

            if (data.success) {
                setUsers(data.data);
                setFilteredUsers(data.data);
            } else {
                console.error("Failed to fetch users:", data.message);
            }
        } catch (error) {
            Sentry.captureException(error);
            handleSessionExpiration(error, setSessionExpired, logout);
            console.error("Error fetching users:", error);
        }
    }, [apiFetch, logout]);

    // Fetch departments for the edit modal
    const fetchDepartments = useCallback(async () => {
        try {
            const response = await apiFetch("/api/Department");
            const data = await response.json();
            if (data.success && data.data) {
                setDepartments(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error fetching departments:", error);
        }
    }, [apiFetch]);

    // Fetch roles for the edit modal
    const fetchRoles = useCallback(async () => {
        try {
            const response = await apiFetch("/api/User/roles");
            const data = await response.json();
            if (data.success && data.data) {
                setRoles(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error fetching roles:", error);
        }
    }, [apiFetch]);

    // Fetch functions for assignment
    const fetchFunctions = useCallback(async () => {
        try {
            const response = await apiFetch("/api/Functions?includeInactive=false");
            const data = await response.json();
            if (data.success && data.data) {
                setFunctions(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error fetching functions:", error);
        }
    }, [apiFetch]);

    useEffect(() => {
        GetUsers();
        fetchDepartments();
        fetchRoles();
        fetchFunctions();
    }, [GetUsers, fetchDepartments, fetchRoles, fetchFunctions]);

    // Re-fetch user list when the parent signals a refresh (e.g. after creating a user)
    useEffect(() => {
        if (refreshTrigger) {
            GetUsers();
        }
    }, [refreshTrigger, GetUsers]);

    useEffect(() => {
        let result = [...users];

        // Filter by active status
        if (showOnlyActive) {
            result = result.filter(user => user.isActive);
        }

        // Apply search filter
        if (searchTerm.trim() !== "") {
            const search = searchTerm.toLowerCase();
            result = result.filter(user =>
                user.userEmail.toLowerCase().includes(search) ||
                user.firstName.toLowerCase().includes(search) ||
                user.lastName.toLowerCase().includes(search)
            );
        }

        // Sort alphabetically by full name (first name then last name) so the
        // table always displays in a predictable order regardless of API return order.
        result.sort((a, b) => {
            const FullNameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
            const FullNameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
            return FullNameA.localeCompare(FullNameB);
        });

        setFilteredUsers(result);
    }, [users, showOnlyActive, searchTerm]);

    // Toggle showing only active users
    const toggleActiveFilter = () => {
        setShowOnlyActive(!showOnlyActive);
    };

    // Handle search input
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    // Open edit modal for a user
    const handleEditUser = async (user) => {
        try {
            // Fetch detailed user info
            const response = await apiFetch(`/api/User/${user.userID}/details`);
            const data = await response.json();
            
            if (data.success && data.data) {
                const userDetails = data.data;
                setEditingUser(userDetails);
                setEditForm({
                    email: userDetails.userEmail || '',
                    firstName: userDetails.firstName || '',
                    lastName: userDetails.lastName || '',
                    phoneNumber: userDetails.phoneNumber || '',
                    roleID: userDetails.roleID,
                    isActive: userDetails.isActive ?? true,
                    departmentID: userDetails.departmentID,
                    managerUserID: userDetails.managerUserID,
                    isDirector: userDetails.isDirector || false,
                    homeDashboardRoute: userDetails.homeDashboardRoute || '/home',
                    accountExpiryDate: formatExpiryDateForInput(userDetails.accountExpiryDate)
                });
                // Filter managers - exclude the user being edited, sort alphabetically
                setManagers([...users.filter(u => u.userID !== userDetails.userID && u.isActive)]
                    .sort((a, b) => (`${a.firstName || ''} ${a.lastName || ''}`).localeCompare(`${b.firstName || ''} ${b.lastName || ''}`)));
                
                // Fetch user's assigned functions
                try {
                    const functionsResponse = await apiFetch(`/api/Functions/user/${user.userID}`);
                    const functionsData = await functionsResponse.json();
                    if (functionsData.success && functionsData.data) {
                        setUserFunctions(functionsData.data);
                    } else {
                        setUserFunctions([]);
                    }
                } catch (error) {
                    Sentry.captureException(error);
                    console.error("Error fetching user functions:", error);
                    setUserFunctions([]);
                }
                
                setShowEditModal(true);
            } else {
                toast.error("Failed to load user details");
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error loading user details:", error);
            toast.error("Error loading user details");
        }
    };

    // Close edit modal
    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingUser(null);
        setUserFunctions([]);
        setReenableEmailConflict(false);
        setEditForm({
            email: '',
            firstName: '',
            lastName: '',
            phoneNumber: '',
            roleID: null,
            isActive: true,
            departmentID: null,
            managerUserID: null,
            isDirector: false,
            homeDashboardRoute: '/home',
            accountExpiryDate: ''
        });
    };

    // Save user changes
    const handleSaveUser = async () => {
        if (!editingUser) return;

        setSavingUser(true);
        try {
            const response = await apiFetch(`/api/User/${editingUser.userID}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: editForm.email !== editingUser.userEmail ? editForm.email : undefined,
                    firstName: editForm.firstName,
                    lastName: editForm.lastName,
                    phoneNumber: editForm.phoneNumber,
                    roleID: editForm.roleID,
                    isActive: editForm.isActive,
                    departmentID: editForm.departmentID === '' ? 0 : editForm.departmentID,
                    managerUserID: editForm.managerUserID === '' ? 0 : editForm.managerUserID,
                    isDirector: editForm.isDirector,
                    homeDashboardRoute: editForm.homeDashboardRoute,
                    accountExpiryDate: editForm.accountExpiryDate 
                        ? new Date(editForm.accountExpiryDate).toISOString() 
                        : null
                })
            });

            const data = await response.json();
            if (data.success) {
                setReenableEmailConflict(false);
                toast.success("User updated successfully");
                GetUsers(); // Refresh the list
                
                // If we just updated the currently logged-in user, refresh their context data
                // This ensures settings like HomeDashboardRoute take effect immediately
                const currentUserID = currentUser?.UserID || currentUser?.userID;
                if (currentUserID && editingUser.userID === currentUserID) {
                    await refreshUser();
                }
                
                handleCloseEditModal();
            } else {
                // Detect re-enable email conflict via stable errorCode (avoids brittle string matching)
                if (data.errorCode === 'REENABLE_EMAIL_CONFLICT') {
                    setReenableEmailConflict(true);
                }
                toast.error(data.message || "Failed to update user");
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error updating user:", error);
            toast.error("Error updating user");
        } finally {
            setSavingUser(false);
        }
    };

    // Assign function to user
    const handleAssignFunction = async (functionID) => {
        if (!editingUser) return;

        try {
            const response = await apiFetch('/api/Functions/assign', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userID: editingUser.userID,
                    functionID: functionID,
                    assignedByUserID: currentUser?.userID || currentUser?.UserID
                })
            });

            const data = await response.json();
            if (data.success) {
                toast.success("Function assigned successfully");
                // Refresh user functions
                const functionsResponse = await apiFetch(`/api/Functions/user/${editingUser.userID}`);
                const functionsData = await functionsResponse.json();
                if (functionsData.success && functionsData.data) {
                    setUserFunctions(functionsData.data);
                }
            } else {
                toast.error(data.message || "Failed to assign function");
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error assigning function:", error);
            toast.error("Error assigning function");
        }
    };

    // Unassign function from user
    const handleUnassignFunction = async (userToFunctionID) => {
        if (!editingUser) return;

        try {
            const response = await apiFetch(`/api/Functions/unassign/${userToFunctionID}`, {
                method: 'DELETE'
            });

            const data = await response.json();
            if (data.success) {
                toast.success("Function unassigned successfully");
                // Refresh user functions
                setUserFunctions(prev => prev.filter(f => f.userToFunctionID !== userToFunctionID));
            } else {
                toast.error(data.message || "Failed to unassign function");
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error unassigning function:", error);
            toast.error("Error unassigning function");
        }
    };

    // Resend password setup email
    const handleResendPasswordEmail = async () => {
        if (!editingUser) return;

        setSendingPasswordEmail(true);
        try {
            const response = await apiFetch(`/api/User/${editingUser.userID}/resend-password-setup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await response.json();
            if (data.success) {
                toast.success(data.message || "Password setup email sent successfully");
            } else {
                toast.error(data.message || "Failed to send password setup email");
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error sending password setup email:", error);
            toast.error("Error sending password setup email");
        } finally {
            setSendingPasswordEmail(false);
        }
    };

    // Get department name by ID
    const getDepartmentName = (deptId) => {
        if (!deptId) return 'None';
        const dept = departments.find(d => d.departmentID === deptId);
        return dept ? dept.departmentName : 'Unknown';
    };

    // Get role name by ID
    const getRoleName = (roleId) => {
        const role = roles.find(r => r.roleID === roleId);
        return role ? role.roleName : 'Unknown';
    };

    // Render expiry date cell
    const renderExpiryDate = (accountExpiryDate) => {
        const formatted = formatExpiryDateForDisplay(accountExpiryDate);
        if (formatted) {
            return (
                <span className="expiry-date" title={formatted.tooltip}>
                    {formatted.display}
                </span>
            );
        }
        return <span className="no-expiry">No expiry</span>;
    };

    if (sessionExpired) {
        return (
            <div className="error-message">
                <p>Your session has expired. Please log in again.</p>
                <button onClick={() => navigate('/')}>Go to Login</button>
            </div>
        );
    }

    return (
        <div className="user-management-page">
<div className="page-header">
                <h1>
                    <FaUsers /> User Management
                </h1>
                <p className="page-description">
                    View and manage user accounts, departments, and manager assignments.
                </p>
            </div>

            <div className="controls-bar">
                <div className="filter-controls">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={showOnlyActive}
                            onChange={toggleActiveFilter}
                        />
                        <span>Show only active users</span>
                    </label>
                </div>

                <div className="search-box">
                    <FaSearch />
                    <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                </div>
            </div>

            <div className="results-count">
                Showing {filteredUsers.length} of {users.length} users
            </div>

            <div className="users-table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Department</th>
                            <th>Status</th>
                            <th>Expiry Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map((user) => (
                            <tr key={user.userID} className={!user.isActive ? 'inactive-row' : ''}>
                                <td>
                                    <div className="user-name">
                                        <span className="name-text">
                                            {user.firstName} {user.lastName}
                                        </span>
                                        {user.isDirector && (
                                            <span className="badge badge-director" title="Director">
                                                <FaStar />
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td>{user.userEmail}</td>
                                <td>{getRoleName(user.roleID)}</td>
                                <td>{getDepartmentName(user.departmentID)}</td>
                                <td>
                                    <span className={`badge ${user.isActive ? 'badge-success' : 'badge-secondary'}`}>
                                        {user.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    {renderExpiryDate(user.accountExpiryDate)}
                                </td>
                                <td>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => handleEditUser(user)}
                                        title="Edit User"
                                    >
                                        <FaEdit /> Edit
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Edit User Modal */}
            {showEditModal && editingUser && (
                <div className="modal-backdrop" onClick={handleCloseEditModal}>
                    <div className="modal-content edit-user-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-header-content">
                                <h2><FaUser className="header-icon" /> Edit User</h2>
                                <span className="user-email-subtitle">{editingUser.userEmail}</span>
                            </div>
                            <button className="modal-close" onClick={handleCloseEditModal}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className="modal-body">
                            {/* Personal Information Section */}
                            <div className="form-section">
                                <div className="section-header">
                                    <FaUser className="section-icon" />
                                    <span>Personal Information</span>
                                </div>
                                <div className="form-row">
                                    <div className="form-group full-width">
                                        <label>Email Address</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={editForm.email}
                                            onChange={(e) => { setReenableEmailConflict(false); setEditForm(f => ({ ...f, email: e.target.value })); }}
                                                placeholder="Enter email address"
                                            />
                                            {reenableEmailConflict && (
                                                <small className="help-text" style={{ color: 'var(--danger, #dc3545)', marginTop: '4px', display: 'block' }}>
                                                    This email address is already in use by an active account. Change it to a unique address before re-enabling.
                                                </small>
                                            )}
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>First Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editForm.firstName}
                                            onChange={(e) => setEditForm(f => ({ ...f, firstName: e.target.value }))}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Last Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editForm.lastName}
                                            onChange={(e) => setEditForm(f => ({ ...f, lastName: e.target.value }))}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Phone Number</label>
                                        <input
                                            type="tel"
                                            className="form-control"
                                            value={editForm.phoneNumber}
                                            onChange={(e) => setEditForm(f => ({ ...f, phoneNumber: e.target.value }))}
                                            placeholder="Enter phone number"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Role</label>
                                        <select
                                            className="form-control"
                                            value={editForm.roleID || ''}
                                            onChange={(e) => setEditForm(f => ({ ...f, roleID: e.target.value ? parseInt(e.target.value) : null }))}
                                        >
                                            <option value="">-- Select Role --</option>
                                            {roles.map(role => (
                                                <option key={role.roleID} value={role.roleID}>
                                                    {role.roleName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Organization Section */}
                            <div className="form-section">
                                <div className="section-header">
                                    <FaBuilding className="section-icon" />
                                    <span>Organization</span>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Department</label>
                                        <select
                                            className="form-control"
                                            value={editForm.departmentID || ''}
                                            onChange={(e) => setEditForm(f => ({ ...f, departmentID: e.target.value ? parseInt(e.target.value) : null }))}
                                        >
                                            <option value="">-- No Department --</option>
                                            {departments.map(dept => (
                                                <option key={dept.departmentID} value={dept.departmentID}>
                                                    {dept.departmentName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Manager</label>
                                        <select
                                            className="form-control"
                                            value={editForm.managerUserID || ''}
                                            onChange={(e) => setEditForm(f => ({ ...f, managerUserID: e.target.value ? parseInt(e.target.value) : null }))}
                                            disabled={editForm.isDirector}
                                        >
                                            <option value="">-- No Manager --</option>
                                            {managers.map(user => (
                                                <option key={user.userID} value={user.userID}>
                                                    {user.firstName} {user.lastName}
                                                </option>
                                            ))}
                                        </select>
                                        {editForm.isDirector && (
                                            <small className="help-text">Directors do not have a manager.</small>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Functions Section */}
                            <div className="form-section">
                                <div className="section-header">
                                    <FaSitemap className="section-icon" />
                                    <span>Functions</span>
                                </div>
                                <div className="functions-assignment">
                                    <p className="help-text">
                                        Assign this user to functions (work responsibilities like Debtors, Creditors, IT Support, etc.)
                                    </p>
                                    
                                    {/* Assigned Functions */}
                                    <div className="assigned-functions">
                                        {userFunctions.length > 0 ? (
                                            <div className="function-tags">
                                                {userFunctions.map(uf => (
                                                    <div key={uf.userToFunctionID} className="function-tag">
                                                        <span className="function-name">{uf.functionName}</span>
                                                        <button
                                                            type="button"
                                                            className="remove-btn"
                                                            onClick={() => handleUnassignFunction(uf.userToFunctionID)}
                                                            title="Remove function"
                                                        >
                                                            <FaTimes />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="no-functions">No functions assigned yet</p>
                                        )}
                                    </div>

                                    {/* Add Function Dropdown */}
                                    <div className="form-group">
                                        <label>Add Function</label>
                                        <select
                                            className="form-control"
                                            onChange={(e) => {
                                                if (e.target.value) {
                                                    handleAssignFunction(parseInt(e.target.value));
                                                    e.target.value = ''; // Reset dropdown
                                                }
                                            }}
                                        >
                                            <option value="">-- Select Function to Add --</option>
                                            {functions
                                                .filter(func => !userFunctions.some(uf => uf.functionID === func.functionID))
                                                .map(func => (
                                                    <option key={func.functionID} value={func.functionID}>
                                                        {func.functionName}
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Status & Settings Section */}
                            <div className="form-section">
                                <div className="section-header">
                                    <FaCog className="section-icon" />
                                    <span>Status & Settings</span>
                                </div>
                                <div className="form-row status-row">
                                    <label className={`checkbox-card${reenableEmailConflict ? ' checkbox-card--conflict' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={editForm.isActive}
                                            onChange={(e) => setEditForm(f => ({ ...f, isActive: e.target.checked }))}
                                        />
                                        <div className="checkbox-content">
                                            <span className="checkbox-title">Active User</span>
                                            <span className="checkbox-desc">
                                                {reenableEmailConflict
                                                    ? 'Update the email address above before re-enabling this account.'
                                                    : 'User can log in to the system'}
                                            </span>
                                        </div>
                                    </label>
                                    <label className="checkbox-card">
                                        <input
                                            type="checkbox"
                                            checked={editForm.isDirector}
                                            onChange={(e) => {
                                                const isDirector = e.target.checked;
                                                setEditForm(f => ({ 
                                                    ...f, 
                                                    isDirector,
                                                    managerUserID: isDirector ? null : f.managerUserID 
                                                }));
                                            }}
                                        />
                                        <div className="checkbox-content">
                                            <span className="checkbox-title">Director</span>
                                            <span className="checkbox-desc">No manager above this user</span>
                                        </div>
                                    </label>
                                </div>

                                <div className="form-row">
                                    <div className="form-group full-width">
                                        <label>Home Page</label>
                                        <select
                                            className="form-control"
                                            value={editForm.homeDashboardRoute || '/home'}
                                            onChange={(e) => setEditForm(f => ({ ...f, homeDashboardRoute: e.target.value }))}
                                        >
                                            {dashboardOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                        <small className="help-text">Select which dashboard this user sees when clicking the logo.</small>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group full-width">
                                        <label>Account Expiry Date & Time</label>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            value={editForm.accountExpiryDate}
                                            onChange={(e) => setEditForm(f => ({ ...f, accountExpiryDate: e.target.value }))}
                                            placeholder="Set expiration date and time"
                                        />
                                        <small className="help-text">
                                            Set an expiry date for users who have submitted resignation notice. 
                                            Leave blank for no expiry. User will be prevented from logging in after this date/time.
                                        </small>
                                    </div>
                                </div>
                            </div>

                            {/* Password Setup Section */}
                            <div className="form-section password-section">
                                <div className="section-header">
                                    <FaKey className="section-icon" />
                                    <span>Password Setup</span>
                                </div>
                                <div className="password-action-card">
                                    <div className="password-info">
                                        <p className="password-description">
                                            Send a new password setup link to the user's email. The link will expire in 24 hours.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-password-reset"
                                        onClick={handleResendPasswordEmail}
                                        disabled={sendingPasswordEmail}
                                    >
                                        {sendingPasswordEmail ? (
                                            <>
                                                <FaSpinner className="fa-spin" /> Sending...
                                            </>
                                        ) : (
                                            <>
                                                <FaEnvelope /> Resend Password Email
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                onClick={handleCloseEditModal}
                                disabled={savingUser}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleSaveUser}
                                disabled={savingUser}
                            >
                                {savingUser ? (
                                    <>
                                        <FaSpinner className="fa-spin" /> Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaSave /> Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewUsers;